/**
 * Regex pattern for valid product name characters.
 * Allows: letters (any script), numbers, spaces, parentheses, hyphens, periods, and percent signs.
 */
const VALID_PRODUCT_NAME_PATTERN = /^[\p{L}\p{N}\p{Zs}().\-%]+$/u;

const PRODUCT_NAME_INVALID_CHARS_MESSAGE =
  "Only letters, numbers, spaces, parentheses (), hyphens (-), periods (.), and percent signs (%) are allowed (e.g., 'Product-Name (v2.0)').";

/**
 * Validates that a product name contains only allowed characters.
 * Allowed: letters, numbers, spaces, parentheses, hyphens, periods, and percent signs.
 *
 * @returns An error message string if invalid, or `false` if valid.
 */
export function validateProductNameCharacters(
  value: string | null | undefined,
): string | false {
  if (!value || typeof value !== 'string') {
    return false;
  }

  if (!VALID_PRODUCT_NAME_PATTERN.test(value)) {
    return PRODUCT_NAME_INVALID_CHARS_MESSAGE;
  }

  return false;
}

/**
 * Validates a product name for both minimum length/numeric-only rules
 * and allowed character rules.
 *
 * @returns An error message string if invalid, or `false` if valid.
 */
export function validateProductName(
  value: string | null | undefined,
): string | false {
  if (
    !value ||
    (typeof value === 'object' && (value as unknown as unknown[]).length === 0)
  ) {
    return 'Product names should be longer than 3 characters and not all numeric';
  }

  if (typeof value === 'string' && (value.length <= 3 || !Number.isNaN(Number(value)))) {
    return 'Product names should be longer than 3 characters and not all numeric';
  }

  const charError = validateProductNameCharacters(value);
  if (charError) {
    return charError;
  }

  return false;
}
